import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconPlus, IconCheck, IconWand, IconEdit } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function PacienteView() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const { patientId } = useParams();
  const [patient, setPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tests, setTests] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [raw, setRaw] = useState("");
  const [error, setError] = useState("");
  const [structuring, setStructuring] = useState(null); // session id being structured

  const load = async () => {
    const [p, s, ta] = await Promise.all([
      api.get(`/sintonia/patients/${patientId}`),
      api.get(`/sintonia/patients/${patientId}/sessions`),
      api.get(`/sintonia/tests/applications?patient_id=${patientId}`),
    ]);
    setPatient(p.data); setSessions(s.data); setTests(ta.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const addSession = async () => {
    if (!raw.trim()) return;
    setError("");
    try {
      await api.post(`/sintonia/patients/${patientId}/sessions`, { raw_notes: raw.trim() });
      setRaw(""); setShowNew(false); await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const structure = async (sid) => {
    setStructuring(sid); setError("");
    try {
      await api.post(`/sintonia/sessions/${sid}/structure`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setStructuring(null); }
  };

  if (!patient) return <div className="wrap-app py-10 text-tert font-mono text-[11px] uppercase">Cargando…</div>;

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app/sintonia")} data-testid="pv-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Sintonía" : "Sintonía"}
        </button>
        <span className="t">{lang === "es" ? "EXPEDIENTE" : "PATIENT FILE"}</span>
      </div>
      <div className="wrap-app max-w-[860px] py-10">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-teal-deep text-white flex items-center justify-center font-display text-[18px]">
            {patient.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="font-display text-[22px] font-medium">{patient.name}</h1>
            <p className="text-tert text-[13px]">{patient.email || "—"} · {patient.phone || "—"}</p>
            {patient.condition && <span className="tag tag-admin mt-2 inline-block">{patient.condition}</span>}
          </div>
        </div>

        {patient.notes && <p className="text-muted text-[14px] mt-4 leading-relaxed">{patient.notes}</p>}

        <div className="flex items-center justify-between mt-8">
          <p className="lbl m-0">{lang === "es" ? "Sesiones" : "Sessions"}</p>
          <button className="btn text-[12px]" onClick={() => setShowNew((v) => !v)} data-testid="session-new">
            <IconPlus size={13} /> {lang === "es" ? "Nueva sesión" : "New session"}
          </button>
        </div>

        {showNew && (
          <div className="card mt-3">
            <p className="text-[12.5px] text-muted mb-2">
              {lang === "es" ? "Escribe o dicta la nota. Luego el Sistema Inteligente puede estructurarla en SOAP." : "Type or dictate. Smart System will structure it in SOAP."}
            </p>
            <textarea className="textarea" rows="5" value={raw} onChange={(e) => setRaw(e.target.value)} data-testid="session-raw" />
            {error && <p className="text-errtext text-[13px] mt-2">{error}</p>}
            <button className="btn mt-2" onClick={addSession} data-testid="session-save">
              <IconCheck size={13} /> {lang === "es" ? "Guardar" : "Save"}
            </button>
          </div>
        )}

        <div className="space-y-3 mt-4">
          {sessions.map((s) => (
            <div key={s.id} className="card" data-testid={`session-${s.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-[11px] text-tert uppercase tracking-widest">
                  {new Date(s.date).toLocaleString(lang === "es" ? "es-HN" : "en-US")}
                </p>
                <button className="btn-ghost btn text-[11px]" onClick={() => structure(s.id)} disabled={structuring === s.id || !s.raw_notes} data-testid={`session-structure-${s.id}`}>
                  <IconWand size={11} /> {structuring === s.id ? "..." : (lang === "es" ? "Estructurar SOAP" : "Structure SOAP")}
                </button>
              </div>
              {s.raw_notes && <p className="text-[13.5px] leading-relaxed italic text-muted mb-3">"{s.raw_notes}"</p>}
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {["S", "O", "A", "P"].map((k) => (
                  <div key={k} className="border-l-2 border-teal-soft pl-3">
                    <p className="lbl m-0 mb-1">{k === "S" ? "Subjetivo" : k === "O" ? "Objetivo" : k === "A" ? "Análisis" : "Plan"}</p>
                    <p className="text-[13.5px] leading-relaxed">{s.soap?.[k] || <span className="text-tert italic">— vacío —</span>}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {sessions.length === 0 && <p className="text-tert text-[13px]">{lang === "es" ? "Sin sesiones aún." : "No sessions yet."}</p>}
        </div>

        {tests.length > 0 && (
          <>
            <p className="lbl mt-10">{lang === "es" ? "Tests aplicados" : "Tests applied"}</p>
            <div className="space-y-2">
              {tests.map((t) => (
                <div key={t.id} className="card flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[14px]">{t.test_name}</p>
                    <p className="text-tert text-[12px]">{new Date(t.applied_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-[18px] font-medium">{t.score}/{t.max_score}</p>
                    <span className="tag">{t.band}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
